const TILE_COLORS = {
  violet: "bg-[rgba(139,92,246,0.22)] text-[#C4B5FD]",
  cyan: "bg-[rgba(6,182,212,0.22)] text-[#67E8F9]",
  emerald: "bg-[rgba(16,185,129,0.22)] text-[#6EE7B7]",
  amber: "bg-[rgba(245,158,11,0.22)] text-[#FCD34D]",
};

function DiscoTile({ icon, color, title, text }) {
  return (
    <div className="p-[1.15rem_1.1rem] rounded-lg bg-[rgba(7,17,31,0.55)] border border-white/[.08] transition-colors hover:border-gold/[.45] hover:bg-gold/[.08]">
      <div
        className={`w-10 h-10 rounded-[10px] flex items-center justify-center mb-3 text-[1.1rem] ${TILE_COLORS[color]}`}
      >
        <i className={`bi ${icon}`} />
      </div>
      <h4 className="text-[0.98rem] font-bricolage font-semibold mb-[0.35rem]">
        {title}
      </h4>
      <p className="m-0 text-[0.84rem] leading-[1.5] text-white/50">{text}</p>
    </div>
  );
}

export default function Discovery() {
  return (
    <section
      id="discovery"
      className="relative overflow-hidden bg-ink text-white py-[5.5rem]"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 50% 45% at 85% 20%, rgba(201, 164, 92, 0.16), transparent 55%), radial-gradient(ellipse 40% 40% at 10% 80%, rgba(18, 184, 154, 0.12), transparent 50%)",
        }}
      />
      <div className="container mx-auto max-w-[1140px] px-4 relative z-[1]">
        <p className="inline-block text-[0.78rem] font-semibold tracking-[0.12em] uppercase text-gold-bright mb-[0.85rem]">
          Data Discovery &amp; Classification
        </p>
        <h2 className="font-bricolage text-[clamp(1.85rem,3.2vw,2.45rem)] mb-3 text-white">
          Find personal data wherever it lives
        </h2>
        <p className="text-[1.08rem] leading-[1.7] text-white/[.68] w-full">
          OneDPDP discovers and classifies personal data across your entire
          estate — using agent-based or agentless collection — across both
          structured systems and unstructured repositories. From databases,
          warehouses and business applications to documents, emails, file shares
          and object storage, classification creates a living map of personal
          and sensitive personal data. That map feeds ROPA, DSAR fulfilment,
          PIAs, Consent Variance and risk decisions automatically — so every
          compliance workflow runs on what you actually hold, not what you
          assume you hold.
        </p>

        <div className="mt-6 mb-2">
          <a
            href="https://dataforesight.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-[0.4rem] bg-teal hover:bg-teal-bright text-white font-semibold rounded px-[1.15rem] py-[0.58rem] text-[0.92rem] transition-all hover:-translate-y-px"
          >
            Explore more <i className="bi bi-arrow-up-right" />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-12 relative z-[1]">
          <div className="p-[1.85rem_1.75rem] rounded-[10px] bg-white/[.04] border border-white/10">
            <h3 className="text-[1.25rem] font-bricolage font-semibold mb-[0.45rem] flex items-center gap-[0.65rem]">
              <i className="bi bi-hdd-network text-[1.25rem] text-[#60A5FA]" />{" "}
              Discovery methods
            </h3>
            <p className="text-white/[.55] text-[0.95rem] leading-[1.55] mb-5">
              Deploy the approach that fits each environment — deep
              introspection where you control hosts, zero-footprint where you
              cannot.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-[0.85rem]">
              <DiscoTile
                icon="bi-cpu"
                color="violet"
                title="Agent-based"
                text="Lightweight agents on servers and endpoints for deep scan, continuous monitoring and near real-time classification."
              />
              <DiscoTile
                icon="bi-cloud-arrow-down"
                color="cyan"
                title="Agentless"
                text="API and connector-driven discovery for cloud apps, SaaS and managed databases — no software to install on every host."
              />
            </div>
          </div>

          <div className="p-[1.85rem_1.75rem] rounded-[10px] bg-white/[.04] border border-white/10">
            <h3 className="text-[1.25rem] font-bricolage font-semibold mb-[0.45rem] flex items-center gap-[0.65rem]">
              <i className="bi bi-diagram-3 text-[1.25rem] text-[#2DD4BF]" />{" "}
              Data coverage
            </h3>
            <p className="text-white/[.55] text-[0.95rem] leading-[1.55] mb-5">
              Classify personal and sensitive personal data wherever fiduciaries
              and processors actually store it.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-[0.85rem]">
              <DiscoTile
                icon="bi-table"
                color="emerald"
                title="Structured data"
                text="Databases, warehouses, data lakes and business applications — column-level and field-level personal data patterns."
              />
              <DiscoTile
                icon="bi-file-earmark-richtext"
                color="amber"
                title="Unstructured data"
                text="Documents, emails, shares, object storage and collaboration tools — content-aware detection for PDFs, Office files and more."
              />
            </div>
          </div>
        </div>

        <div className="mt-8 p-[1.35rem_1.6rem] rounded-lg border-l-[3px] border-gold bg-white/[.04] text-white/[.7] text-[0.98rem] leading-[1.55]">
          <strong className="text-white">Why it matters for DPDP:</strong> You
          cannot protect, report or erase what you cannot find. Discovery and
          classification ground Consent, DSAR, ROPA and Breach response in a
          living map of personal data — so compliance decisions reflect reality,
          not assumptions.
        </div>
      </div>
    </section>
  );
}
